import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import SearchBlock from '../../components/Search/SearchBlock';
import PredictiveSearchHeader from '../../components/PredictiveSearch/PredictiveSearchHeader';
import SearchBannerAd from '../../components/Search/SearchBannerAd';
import {
	getSearchResults, getSearchFilters, getSelectedFilters, convertFiltersToQueryString,
} from '../../lib/fetcher';
import { useInfiniteSearch } from '../../utils/useInfiniteSearch';
import { RECIPE_FILTER_QUERY as SEARCH_FILTER_QUERY } from '../../components/RecipeFilter/RecipeFilterQuery';
import { RECIPE_FILTERS_DATA as SEARCH_FILTER_DATA } from '../../components/RecipeFilter/RecipeFiltersData';
import { TAG_PAGES_QUERY } from '../../components/Tags/TagPagesQuery';
import SeoNext from '../../components/Seo/SeoNext';
import SeoJsonLd, { parseForSeo } from '../../components/Seo/Json/SeoJsonLd';
import AdobeAnalyticsDataLayer from '../../components/AdobeAnalytics/AdobeAnalyticsDataLayer';
import { resetScrollPaths, restoreScrollPos, useScrollRestoration } from '../../utils/useScrollRestoration';

// Util imports
import { initializeApollo } from '../../utils/apolloClient';
import { isUsTraffic } from '../../utils/geoBlock';
import { copySort } from '../../utils/utility';
import { handleSearchData } from '../../utils/analytics';

const SEARCH_API = process.env.NEXT_PUBLIC_SEARCH_API;

export default function Search({
	initialFilters = {},
	initialPosts = {},
	initialSelection = {},
	mostRecentPosts = [],
}) {
	const [fallbackData, setFallbackData] = useState([initialPosts]);
	const [selection, setSelection] = useState(initialSelection);
	const [initialLoad, setInitialLoad] = useState(true);

	useEffect(() => {
		if (initialLoad) setInitialLoad(false);
	}, [initialLoad]);

	const router = useRouter();

	// Get the term position from the url
	const { position } = router.query;

	useScrollRestoration(router);

	const {
		error,
		found,
		isLoading,
		isValidating,
		isEnd,
		results,
		size,
		setSize,
	} = useInfiniteSearch({
		fallbackData,
		selection,
	});

	const makeSearchUrl = (searchOptions) => {
		const hasContentFilter = Object.keys(searchOptions?.show).length !== 0 && searchOptions?.show.constructor === Object;
		const hasFilters = searchOptions?.filters && searchOptions?.filters.length > 0;

		let url = '/search';

		if (!searchOptions.term) {
			return url;
		}

		url = `${url}/${encodeURIComponent(searchOptions.term)}`;

		const qsArr = [];

		const pageQS = `page=${searchOptions.page || 1}`;
		qsArr.push(pageQS);

		const filtersQS = convertFiltersToQueryString(searchOptions.filters || []);
		if (filtersQS) qsArr.push(filtersQS);

		if (searchOptions.show.name) {
			const showQS = `${searchOptions.show.name}=${searchOptions.show.value}`;
			qsArr.push(showQS);
		}

		// Set the term position when predictive search is enabled
		const termPosition = `position=${searchOptions.position}`;
		qsArr.push(termPosition);

		url = `${url}?${qsArr.join('&')}`;

		// If search has filters update the url by removing the position
		if (hasContentFilter || hasFilters || url.includes('&position=undefined')) {
			url = url.replace(/&position=[^&\s]+/g, '');
		}

		return url;
	};

	const makeSelection = ({ term, page, ...filtersQuery }) => {
		const selected = getSelectedFilters(filtersQuery, initialFilters, SEARCH_FILTER_DATA);

		const newSelection = {
			term,
			filters: selected.filters,
			page: page ? Number(page) : 1,
			count: 12,
			show: selected.show,
		};

		return newSelection;
	};

	const changeRoute = (newSelection) => {
		const updatedSelection = {
			...selection,
			...newSelection,
		};
		const url = makeSearchUrl(updatedSelection);
		router.push(url, null, { shallow: true });
	};

	const searchHandler = (newTerm, newPosition) => {
		if (newTerm === selection.term) {
			return;
		}

		setFallbackData([]);

		changeRoute({
			term: newTerm,
			filters: [],
			show: {},
			page: 1,
			position: newPosition,
		});
	};

	const updateSearchFilters = (newFilters) => {
		changeRoute({
			filters: newFilters,
			page: 1,
		});
	};

	const updatePostFilter = (newShowContent) => {
		changeRoute({
			show: newShowContent,
			page: 1,
		});
	};

	const onLoadMore = () => {
		const nextPage = Number(size) + 1;
		changeRoute({
			page: nextPage,
		});
	};

	useEffect(() => {
		const newSelection = makeSelection(router.query);
		if (size !== newSelection.page) {
			setSize(newSelection.page);
		}
		setSelection({ ...selection, ...newSelection });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.query]);

	useEffect(() => {
		if (!initialLoad) {
			return;
		}

		handleSearchData();

		const preserveScroll = () => {
			const pathName = router.asPath.split('?')[0];
			restoreScrollPos(router.asPath);
			resetScrollPaths(pathName);
		};

		// wait for the ads before preserving scroll state
		setTimeout(preserveScroll, 1000);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialLoad]);

	const searchTerm = selection.term;
	const title = searchTerm.charAt(0).toUpperCase() + searchTerm.substring(1, searchTerm.length);
	const seo = parseForSeo(initialPosts.result, title);
	// eslint-disable-next-line
	const description = `If you're searching for the best recipes, cooking tips and news about ${title}, look no further than right here on Flavour Network.`;
	const page = `Best ${title} Recipes & Cooking Tips | Flavour Network`;

	return (
		<>
			<SeoNext
				descriptionInfo={description}
				ogDescriptionInfo={description}
				ogTitleInfo={page}
				titleInfo={page}
			/>
			<SeoJsonLd seo={seo} description={description} pagename={page} />
			<AdobeAnalyticsDataLayer
				viewType="archive"
			/>
			<div className="search">
				<SearchBannerAd />
				<PredictiveSearchHeader
					compact
					error={error}
					found={found}
					filters={selection.filters}
					filterItems={initialFilters}
					filtersData={SEARCH_FILTER_DATA}
					isValidating={isValidating}
					onSearch={searchHandler}
					postFilter={selection.show}
					setFilters={updateSearchFilters}
					setPostFilter={updatePostFilter}
					term={selection.term}
					termPosition={position}
					searchInputLabelId="combobox-search-input-label-compact"
					searchId="combobox-search-input-id-compact"
				/>

				{!error && (
					<SearchBlock
						end={isEnd}
						filters={selection.filters}
						isValidating={isValidating}
						loading={isLoading}
						onLoadMore={onLoadMore}
						posts={selection.term ? results : []}
						postFilter={selection.show}
						term={selection.term}
						nextPageHref={makeSearchUrl({ ...selection, page: Number(size) + 1 })}
						mostRecentPosts={mostRecentPosts}
					/>
				)}
			</div>
		</>
	);
}

export async function getServerSideProps(context) {
	const { query } = context;
	const {
		term = '', page = 1, count = 12, ...filtersSelection
	} = query;

	// fetch search data
	let initialPosts = { found_posts: 0, result: [] };

	const searchSelection = {
		term,
		page: Number(page),
		count,
		isUsTraffic: isUsTraffic(context),
	};

	if (term) {
		const params = new URLSearchParams(searchSelection);
		const filterParams = new URLSearchParams(filtersSelection);
		const endpoint = `${SEARCH_API}?${params}&${filterParams}`;
		initialPosts = await getSearchResults(endpoint);
	}

	// fetch fiters
	const initialFilters = await getSearchFilters(SEARCH_FILTER_QUERY);

	const initialSelectedFilters = getSelectedFilters(filtersSelection, initialFilters, SEARCH_FILTER_DATA);

	const initialSelection = { ...searchSelection, ...initialSelectedFilters };

	const apolloClient = initializeApollo();

	const mostRecentPosts = [];

	// query for latest posts
	const latestPostsData = await apolloClient.query({
		query: TAG_PAGES_QUERY,
		variables: {
			postTypes: 'post,ce_recipe',
			limit: 5,
		},
	});

	if (latestPostsData) {
		// get recipe and articles data
		const { data } = latestPostsData;

		if (data?.tagPages) {
			const allPosts = data.tagPages.posts;
			// sort all posts based on the date
			const sortedPosts = copySort(allPosts, (a, b) => {
				const aDate = new Date(a?.date ?? '2000-01-01');
				const bDate = new Date(b?.date ?? '2000-01-01');

				return bDate.getTime() - aDate.getTime();
			});

			// get the first 3 posts which are most recent
			mostRecentPosts.push(...sortedPosts.slice(0, 3));
		}
	}

	return {
		props: {
			initialFilters,
			initialPosts,
			initialSelection,
			mostRecentPosts,
		},
	};
}

Search.propTypes = {
	initialFilters: PropTypes.object,
	initialPosts: PropTypes.object,
	initialSelection: PropTypes.object,
	mostRecentPosts: PropTypes.array,
};
